// Klub / Dotazy k živému vysílání (7.10.2026, zadání Ládi).
//
// Členky v klubu posílají Martě dotazy předem (klub.fitway40.cz/#/vysilani).
// Tady je Láďa s Marťou projdou, během vysílání odškrtnou zodpovězené
// a u každého zůstane, kdy a kdo ho vyřídil. Členka v klubu pak u svého
// dotazu vidí „Zodpovězeno ve vysílání“.
//
// VZHLED podle stránky Členové: jedna karta, filtry jako rozbalovací
// výběry, hlavní akce ikonou v řádku, vedlejší pod třemi tečkami.
import React, { useState } from "react";
import { MessageCircleQuestion, Check, RefreshCw, Copy, Undo2, Trash2, UserRound, EyeOff } from "lucide-react";
import { useApiData, apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { Stranka, RozbalovaciVyber, ErrorNote, SkeletonRows, EmptyState, NabidkaAkci, Stitek } from "../../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice, Pilulka } from "../../components/PrehledDlazdice.jsx";
import { toast } from "../../components/Toast.jsx";

const kratce = (iso) => {
  const d = new Date(iso);
  return `${d.getDate()}. ${d.getMonth() + 1}.`;
};
const sCasem = (iso) => {
  const d = new Date(iso);
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const vysilaniPopis = (id) => {
  const [r, m, dd] = String(id).split("-").map(Number);
  return `${dd}. ${m}. ${r}`;
};

async function post(body) {
  const r = await apiFetch("/api/klub-dotazy", { method: "POST", body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || "Akce selhala");
  return j;
}

function Dotaz({ d, onVyridit, onVratit, onSmazat }) {
  const hotovo = d.stav === "vyrizeny";
  return (
    <div className={`flex items-start gap-3 px-4 py-4 sm:px-5 ${hotovo ? "bg-slate-50/60" : ""}`}>
      <div className="min-w-0 flex-1">
        <p className={`text-[15px] leading-relaxed ${hotovo ? "text-slate-500" : "text-slate-800"}`}>„{d.text}“</p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {d.sJmenem ? (
            <Stitek ton="znacka" ikona={UserRound}>{d.jmeno || d.email}</Stitek>
          ) : (
            <Stitek ton="neutralni" ikona={EyeOff}>
              Bez jména
              <span aria-hidden className="h-3 w-px bg-current opacity-40" />
              <span className="font-semibold">{d.jmeno || d.email}</span>
            </Stitek>
          )}
          <Pilulka>položeno {kratce(d.vytvoreno)}</Pilulka>
          {hotovo && (
            <Stitek ton="ok" ikona={Check}>
              Vyřízeno {sCasem(d.vyrizeno)}
              <span aria-hidden className="h-3 w-px bg-current opacity-40" />
              {d.vyridil}
            </Stitek>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {!hotovo && (
          <button type="button" onClick={() => onVyridit(d)} title="Označit jako vyřízený"
            aria-label="Označit jako vyřízený"
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 transition active:scale-95 sm:h-10 sm:w-10 sm:hover:bg-emerald-100">
            <Check className="h-5 w-5" />
          </button>
        )}
        <NabidkaAkci
          popis="Další volby u dotazu"
          polozky={[
            { klic: "kopirovat", ikona: Copy, popis: "Zkopírovat text dotazu", onClick: () => {
              navigator.clipboard?.writeText(d.text).then(() => toast("Dotaz zkopírován.", "success"));
            } },
            ...(hotovo ? [{ klic: "vratit", ikona: Undo2, popis: "Vrátit mezi nevyřízené", onClick: () => onVratit(d) }] : []),
            { klic: "smazat", ikona: Trash2, popis: "Smazat dotaz", nebezpecna: true, onClick: () => onSmazat(d) },
          ]}
        />
      </div>
    </div>
  );
}

export default function KlubDotazy() {
  const { data, loading, error, refetch } = useApiData("/api/klub-dotazy", { bezKese: true });
  const [vysilani, setVysilani] = useState("nejblizsi");
  const [stav, setStav] = useState("novy");
  const [razeni, setRazeni] = useState("nejstarsi");
  const [mazani, setMazani] = useState(null);

  const dotazy = data?.dotazy || [];
  const terminy = data?.vysilani || [];
  const nejblizsi = terminy.find((t) => t.budouci)?.id || terminy[terminy.length - 1]?.id;
  const zvolene = vysilani === "nejblizsi" ? nejblizsi : vysilani;

  const vTerminu = dotazy.filter((d) => zvolene === "vse" || d.vysilani === zvolene);
  const pocty = {
    novy: vTerminu.filter((d) => d.stav === "novy").length,
    vyrizeny: vTerminu.filter((d) => d.stav === "vyrizeny").length,
  };
  const videt = vTerminu
    .filter((d) => stav === "vse" || d.stav === stav)
    // Nevyřízené vždy nahoře, vyřízené pod nimi; uvnitř podle data položení.
    .sort((a, b) => (a.stav === b.stav ? 0 : a.stav === "novy" ? -1 : 1)
      || (razeni === "nejstarsi" ? 1 : -1) * (new Date(a.vytvoreno) - new Date(b.vytvoreno)));

  async function akce(body, zprava) {
    try { await post(body); toast(zprava, "success"); refetch(); }
    catch (e) { toast(e.message, "error"); }
  }

  return (
    <Stranka>
      <GradientHeader
        title="Dotazy k vysílání"
        icon={MessageCircleQuestion}
        loading={loading}
        napoveda="Dotazy, které členky klubu poslaly Martě předem. Během vysílání je odškrtávejte. Členka pak v klubu u svého dotazu uvidí, že je zodpovězený. Dotazy bez jména se ve vysílání čtou anonymně."
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading }]}
      />

      {error && <ErrorNote onRetry={refetch}>Dotazy se nepodařilo načíst: {String(error)}</ErrorNote>}

      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={MessageCircleQuestion}
          nazev={zvolene === "vse" ? "Všechna vysílání" : `Vysílání ${vysilaniPopis(zvolene)}`}
          podtitulek={`${pocty.novy + pocty.vyrizeny} dotazů, ${pocty.vyrizeny} vyřízených`}
          odznak={`${pocty.novy} čeká`}
          odznakTon={pocty.novy ? "zluty" : "bily"}
        />

        <div className="grid gap-2 border-b border-slate-200 bg-brand-light/60 p-3 sm:grid-cols-3 sm:p-4">
          <RozbalovaciVyber
            stitek="Vysílání"
            aria-label="Které vysílání"
            value={vysilani}
            onChange={setVysilani}
            items={[
              { key: "nejblizsi", label: `Nejbližší (${nejblizsi ? vysilaniPopis(nejblizsi) : "žádné"})` },
              ...terminy.map((t) => ({ key: t.id, label: vysilaniPopis(t.id) })),
              { key: "vse", label: "Všechna" },
            ]}
          />
          <RozbalovaciVyber
            stitek="Stav"
            aria-label="Filtr podle stavu"
            value={stav}
            onChange={setStav}
            items={[
              { key: "novy", label: `Nevyřízené (${pocty.novy})` },
              { key: "vyrizeny", label: `Vyřízené (${pocty.vyrizeny})` },
              { key: "vse", label: `Vše (${pocty.novy + pocty.vyrizeny})` },
            ]}
          />
          <RozbalovaciVyber
            stitek="Řadit"
            aria-label="Řazení dotazů"
            value={razeni}
            onChange={setRazeni}
            items={[
              { key: "nejstarsi", label: "Nejstarší první" },
              { key: "nejnovejsi", label: "Nejnovější první" },
            ]}
          />
        </div>

        {loading && !data && <div className="p-3 sm:p-4"><SkeletonRows rows={5} /></div>}

        {data && (videt.length === 0 ? (
          <EmptyState icon={MessageCircleQuestion}
            title={stav === "novy" ? "Všechno vyřízeno" : "Žádný dotaz"}
            hint={stav === "novy" ? "Na nejbližší vysílání nečeká žádný dotaz." : "Zkus jiné vysílání nebo stav."} />
        ) : (
          <div className="divide-y divide-slate-100">
            {videt.map((d) => (
              <Dotaz key={d.id} d={d}
                onVyridit={(x) => akce({ action: "vyridit", id: x.id }, "Dotaz označen jako vyřízený.")}
                onVratit={(x) => akce({ action: "vratit", id: x.id }, "Dotaz vrácen mezi nevyřízené.")}
                onSmazat={setMazani} />
            ))}
          </div>
        ))}
      </KartaPrehledu>

      <ConfirmDialog
        open={!!mazani}
        title="Smazat dotaz?"
        description="Dotaz zmizí i člence v klubu. Vrátit to nepůjde."
        confirmLabel="Smazat"
        variant="danger"
        onConfirm={() => { akce({ action: "smazat", id: mazani.id }, "Dotaz smazán."); setMazani(null); }}
        onCancel={() => setMazani(null)}
      />
    </Stranka>
  );
}
